import { FC, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { generateUniqueKey } from '@helpers';
import { useGetStoresListNoCacheQuery, useUpdateStoresListMutation } from '@services';
import { AvaStore, JimboResponse } from '@models';
import { showToast } from '@store/slices/notification';
import { addErrorToLog } from '@store/slices/error-log';
import { useAppDispatch } from '@hooks/redux-custom';
import AvaStoresListCsvReader from './AvaStoresListCsvReader';
import { compareStores } from './AvaDepotHours.service';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export type CsvRow = { [key: string]: any };

const AvaStoresListUploadCsvModal: FC<Props> = ({ isOpen, onClose }) => {
  const [invalidHeaders, setInvalidHeaders] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const { data: stores, isLoading } = useGetStoresListNoCacheQuery();
  const [updateStores] = useUpdateStoresListMutation();
  const dispatch = useAppDispatch();
  const hasErrors = () => invalidHeaders.length > 0 || validationErrors.length > 0;

  const clearState = () => {
    setCsvRows([]);
    setInvalidHeaders([]);
    setValidationErrors([]);
  };

  const handleClose = () => {
    clearState();
    onClose();
  };

  const renderErrorAlert = (title: string, errorArray: string[]) => (
    <Alert severity="error" sx={{ my: 2 }}>
      <AlertTitle>{title}</AlertTitle>
      <ul>
        {errorArray.map((item) => (
          <li key={generateUniqueKey()}>{item}</li>
        ))}
      </ul>
    </Alert>
  );

  const addValidationError = (error: string) => {
    if (validationErrors.includes(error)) return;

    const newArray = validationErrors;

    newArray.push(error);
    setValidationErrors(newArray);
  };

  const renderErrors = () =>
    hasErrors() && (
      <>
        <Typography marginTop={2} variant="h6">
          The following errors must be resolved
        </Typography>
        {invalidHeaders.length > 0 && renderErrorAlert('The following headers are invalid', invalidHeaders)}
        {validationErrors.length > 0 && renderErrorAlert('The following values are invalid', validationErrors)}
      </>
    );

  const applyCsvChanges = async () => {
    if (!stores) return;

    const csvStoreCodes = csvRows.map((row) => row.store_code);
    const updatedList = compareStores(stores, csvRows);
    const listsToDelete: Partial<AvaStore>[] = [];

    stores.forEach((store) => {
      if (!csvStoreCodes.includes(store.store_code)) {
        listsToDelete.push(store);
      }
    });

    if (updatedList) {
      updateStores(updatedList)
        .unwrap()
        .then((response) => {
          dispatch(
            showToast({
              message: response,
              type: 'success',
            }),
          );
        })
        .then(handleClose)
        .catch((error: { data: JimboResponse }) => {
          dispatch(addErrorToLog(error?.data));

          dispatch(
            showToast({
              message: 'Failed to update stores.',
              type: 'error',
            }),
          );

          handleClose();
        });
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md">
      <DialogTitle>Upload CSV</DialogTitle>
      <DialogContent sx={{ marginTop: 2 }}>
        <AvaStoresListCsvReader
          addInvalidHeader={(header) => setInvalidHeaders([...invalidHeaders, header])}
          addValidationError={addValidationError}
          clearState={clearState}
          setCsvRows={setCsvRows}
        />
        {renderErrors()}
        {!hasErrors() && csvRows.length > 0 && (
          <Alert severity="success" sx={{ my: 2 }}>
            <AlertTitle>All fields are mapped and ready to upload</AlertTitle>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', my: 2, px: 3 }}>
        <Box>
          <Button type="button" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          {!hasErrors() && (
            <Button
              onClick={() => {
                applyCsvChanges();
              }}
              sx={{ ml: 2 }}
              type="button"
              variant="contained"
              data-testid="ava-stores-list-upload-btn"
            >
              Upload
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AvaStoresListUploadCsvModal;
